import { dispatch } from 'nike-re-framejs';
import forumClient from '../../common/axios/forumClient';
import { translateServerError } from './utils/translateServerError';
import { timeFilterIntervals } from './utils/TimeFilterIntervals';
import { BETTABLE, TIME_3HOURS, TIME_TODAY, TIME_TOMORROW } from './constants';
import { isFunction } from 'lodash';

const setTitleSuggestion = postTitle => db => db.setIn(['betbook', 'newPost', 'title', 'default-text'], postTitle);
const limitsReached = limits => db => db.setIn(['betbook', 'limits', 'reachLimits'], limits);
const setCanHaveAnalysisReward = rewardLimits => db =>
    db.setIn(['betbook', 'limits', 'canHaveAnalysisReward'], rewardLimits);

export const loadTitleSuggestion = (betId, betSelectionCode) => () => {
    forumClient
        .get(`/v5/betbook/title-suggestion?betId=${betId}&betSelectionCode=${betSelectionCode}`)
        .then(title => title && dispatch(setTitleSuggestion(title)));
};

export const loadAnalysesLimits = authorNickname => () => {
    forumClient
        .get(`/v5/betbook/authors/${authorNickname}/analyses-limit-reached`)
        .then(response => response && dispatch(limitsReached(response)));
};

export const loadCanHaveAnalysisReward = authorNickname => () => {
    forumClient
        .get(`/v5/betbook/authors/${authorNickname}/can-have-reward`)
        .then(response => dispatch(setCanHaveAnalysisReward(response)));
};

const setGamingOpportunities = gamingOpportunities => db =>
    db.setIn(['betbook', 'gaming-opportunities'], gamingOpportunities);

export const loadGamingOpportunities = () => {
    forumClient
        .get(`/v5/betbook/gaming-opportunities/bets`)
        .then(opportunities => opportunities && dispatch(setGamingOpportunities(opportunities)));
};

const setGamingOpportunityTickets = gamingOpportunityTickets => db =>
    db.setIn(['betbook', 'gaming-opportunity-tickets'], gamingOpportunityTickets);

export const loadGamingOpportunityTickets = (betId, betSelectionCode) => () => {
    forumClient
        .get(`/v5/betbook/gaming-opportunities/tickets?betId=${betId}&betSelectionCode=${betSelectionCode}`)
        .then(tickets => tickets && dispatch(setGamingOpportunityTickets(tickets)));
};

export const setGamingOpportunityWithTicket = gamingOpportunityWithTicket => db =>
    db.setIn(['betbook', 'gaming-opportunity'], gamingOpportunityWithTicket);

export const loadGamingOpportunityWithTicket = (betId, ticketId) => () => {
    forumClient
        .get(`/v5/betbook/gaming-opportunities/${betId}/${ticketId}`)
        .then(opportunity => opportunity && dispatch(setGamingOpportunityWithTicket(opportunity)));
};

export const setError = error => db => db.setIn(['betbook', 'errors'], error);

export const forumTopicReset = topic => db => db.removeIn(['forum', topic]);

export const createNewPost = (postType, request, createPostPopUp, onPortalError, mobile) => db => {
    forumClient
        .post(`/v5/betbook/${toUrlPart(postType)}`, request)
        .then(() => {
            if (db.getIn(['betbook', 'posts', 'list'])) {
                dispatch(clearBetbook);
            }
            if (db.getIn(['betbook', 'errors'])) {
                dispatch(setError(null));
            }
            if (db.getIn(['forum', 'betbook:new-analysis'])) {
                dispatch(forumTopicReset('betbook:new-analysis'));
            }
            createPostPopUp(postType);
        })
        .catch(error => {
            mobile ? dispatch(setError(error.response.data.code)) : onPortalError(error, translateServerError);
        });
    return db;
};

export const editPost = (postType, request, postId, editPostPopUp, onPortalError, mobile) => db => {
    const { text, title } = request;
    forumClient
        .put(`/v5/betbook/${toUrlPart(postType)}/${postId}`, request)
        .then(() => {
            if (db.getIn(['betbook', 'errors'])) {
                dispatch(setError(null));
            }
            dispatch(db =>
                db
                    .updateIn(['betbook', 'posts', 'list'], list =>
                        list?.map(p => (p.get('id') === Number(postId) ? p.set('title', title).set('text', text) : p))
                    )
                    .removeIn(['betbook', 'requestActive'])
            );
            editPostPopUp(postType, Number(postId));
        })
        .catch(error => {
            mobile ? dispatch(setError(error.response.data.code)) : onPortalError(error, translateServerError);
        });
    return db;
};

const setBetbookFilterConfig = filterConfig => db => db.setIn(['betbook', 'posts', 'filter-config'], filterConfig);

export const fetchBetbookFilterConfig = (postType, expirationTag, sportIds) => {
    const params = {};
    if (expirationTag === TIME_3HOURS || expirationTag === TIME_TODAY || expirationTag === TIME_TOMORROW) {
        if (timeFilterIntervals[expirationTag]) {
            params.expirationFrom = timeFilterIntervals[expirationTag].expirationFrom();
            params.expirationTo = timeFilterIntervals[expirationTag].expirationTo();
        }
    }
    if (expirationTag === BETTABLE) {
        params.bettable = true;
    }
    if (sportIds?.length) {
        params.sportIds = sportIds.join(',');
    }
    forumClient
        .get(`/v5/betbook/${toUrlPart(postType)}/filter-config`, { params })
        .then(filterConfig => filterConfig && dispatch(setBetbookFilterConfig(filterConfig)));
};

const setBetbookFilterResultsCount = resultsCount => db =>
    db.setIn(['betbook', 'posts', 'results-count'], resultsCount);

export const fetchBetbookFilterResultsCount = (betCount, expirationTag, odds, sportIds, stake, postType, win) => {
    const params = {};
    if (expirationTag === TIME_3HOURS || expirationTag === TIME_TODAY || expirationTag === TIME_TOMORROW) {
        if (timeFilterIntervals[expirationTag]) {
            params.expirationFrom = timeFilterIntervals[expirationTag].expirationFrom();
            params.expirationTo = timeFilterIntervals[expirationTag].expirationTo();
        }
    }
    if (expirationTag === BETTABLE) {
        params.bettable = true;
    }
    if (sportIds.length) {
        params.sportIds = sportIds.join(',');
    }
    if (betCount) {
        params.betCountFrom = betCount[0];
        params.betCountTo = betCount[1];
    }
    if (odds) {
        params.oddsFrom = odds[0];
        params.oddsTo = odds[1];
    }
    if (stake) {
        params.stakeFrom = stake[0];
        params.stakeTo = stake[1];
    }
    if (win) {
        params.winFrom = win[0];
        params.winTo = win[1];
    }
    forumClient
        .get(`/v5/betbook/${toUrlPart(postType)}/size`, { params })
        .then(resultsCount => dispatch(setBetbookFilterResultsCount(resultsCount ? resultsCount : 0)));
};

export function incrementPostViews(postType, id, callback) {
    forumClient.post(`/v5/betbook/${toUrlPart(postType)}/${id}/view`).then(() => {
        // optimisticky update
        if (isFunction(callback)) {
            callback(id);
        }
    });
}

const clearBetbook = db => db.deleteIn(['betbook', 'lastParam']);
export const clearCreateAnalysisBetIds = db => db.deleteIn(['betbook', 'create-analysis-betIds']);
export const clearGamingOpportunities = db => db.deleteIn(['betbook', 'gaming-opportunities']);
export const clearGamingOpportunityTickets = db => db.deleteIn(['betbook', 'gaming-opportunity-tickets']);
export const clearGamingOpportunityWithTicket = db => db.deleteIn(['betbook', 'gaming-opportunity']);
export const clearTitleSuggestion = db => db.deleteIn(['betbook', 'newPost', 'title', 'default-text']);

export function toUrlPart(postType) {
    return postType === 'ANALYSIS' ? 'analyses' : postType === 'BLOG' ? 'blogs' : 'tickets';
}

export function setBetbookLastParam(param) {
    return db => db.setIn(['betbook', 'lastParam'], param);
}
